import type { LotDocument } from "./lotDocuments";
import { apiFetch } from "./client";

/**
 * Nalog za otpremu, strana kvaliteta.
 *
 * Prodaja otvara nalog; kvalitet određuje koji LOT sme da se veže za njega. To je poslednja kapija
 * lanca — sve pre nje je merenje, ovde se odlučuje šta izlazi iz fabrike.
 *
 * LOT stoji na STAVCI a ne na nalogu, jer jedan lot ide u više kamiona i jedan kamion nosi više lotova.
 */
export const DISPATCH_ORDER_STATUSES = [
  "DRAFT",
  "AWAITING_QUALITY",
  "RELEASED",
  "ON_HOLD",
  "CANCELLED",
] as const;
export type DispatchOrderStatus = (typeof DISPATCH_ORDER_STATUSES)[number];

export type DispatchOrderLine = {
  id: string;
  position: number;
  erpProductId: string | null;
  productName: string | null;
  quantity: number | null;
  unit: string | null;
  goodsLotId: string | null;
  lotDesignation: string | null;
  lotProducedOn: string | null;
  lotExpiresOn: string | null;
  lotProductName: string | null;
  pallets: number | null;
  released: boolean;
  releasedAt: string | null;
  releasedBy: string | null;
  held: boolean;
  heldAt: string | null;
  heldBy: string | null;
  holdReason: string | null;
  awaitsQuality: boolean;
  /** Šta je kvalitet rekao o LOT-u (usaglašen, SS, neusaglašen); null dok nije ocenjen. */
  lotQualityStatus?: string | null;
  lotQualityNote?: string | null;
  /**
   * LOT-ov artikal se ne poklapa sa artiklom stavke. Portal to ne odbija — sam ERP se u tome ne slaže
   * sa sobom na 1.738 od 40.816 oznaka — ali onaj ko pušta LOT treba to da vidi, a ne da sazna od kupca.
   */
  articleMismatch: boolean;
};

export type DispatchOrder = {
  id: string;
  number: string;
  erpId: string | null;
  issueDate: string;
  companyId: string | null;
  companyName: string | null;
  status: DispatchOrderStatus;
  dispositionDate: string | null;
  vehicle: string | null;
  driver: string | null;
  loadingPlace: string | null;
  deliveryPlace: string | null;
  note: string | null;
  sourceOrderId: string | null;
  sourceOfferId: string | null;
  qualityReleasedAt: string | null;
  lines: DispatchOrderLine[];
  /** Stavke koje nose LOT i još čekaju odluku. */
  awaiting: number;
  /** Broj koji je ERP dao nalogu za utovar (38-…); prazno dok nalog nije u ERP-u. */
  erpDocumentNumber: string | null;
  /** Broj iz portala (NO-…) koji je nalog nosio pre nego što je postao ERP-ov nalog za utovar. */
  portalNumber?: string | null;
  /** Put naloga u ERP: čeka, upisan ili nije primljen; prazno dok ga kvalitet ne pusti celog. */
  erpStatus: "PENDING" | "SENT" | "FAILED" | null;
  erpError: string | null;
};

/** LOT koji kvalitet sme da stavi na stavku: sa stanja magacina, onaj koji pre ističe prvi. */
export type LotOption = {
  goodsLotId: string;
  lot: string | null;
  fields: string | null;
  expiresOn: string | null;
  qualityStatus: string;
  qualityNote: string | null;
  unit: string | null;
  stock: number;
  /** Koliko od toga ova porudžbina već drži (rezervacija ili nalog). */
  heldForOrder: number;
  /** Na stanju manje ono što drže druge porudžbine. */
  available: number;
};

/** Ponuda za stavku i koliko LOT-ova artikla na stanju nije ponuđeno, po razlogu. */
export type LotOptions = {
  erpProductId: string | null;
  domestic: boolean;
  stockReadAt: string | null;
  options: LotOption[];
  expired: number;
  unassessed: number;
  nonconforming: number;
  domesticOnlyAbroad: number;
  heldByOthers: number;
};

/**
 * Šta kvalitet ima o LOT-u kad odlučuje da ga pusti: nalazi uneti u portal, arhiv laboratorije (za LOT, a kad ga nema,
 * za artikal) i dokumenti uz LOT. Arhiv „ocenjen“ broji samo rezultate sa vrednošću i granicom koja važi.
 */
export type LotEvidence = {
  goodsLotId: string;
  readings: { count: number; outOfRange: number; awaitingSignature: number; lastAt: string | null };
  archive: {
    analyses: number;
    lastOn: string | null;
    judged: number;
    outside: number;
    articleAnalyses: number;
    articleLastOn: string | null;
  };
  documents: LotDocument[];
};

export const dispatchOrderQualityApi = {
  /** Nalazi i dokumenti LOT-a, za odluku o puštanju. */
  evidence: (goodsLotId: string) => apiFetch<LotEvidence>(`/dispatch-orders/quality/lots/${goodsLotId}/evidence`),

  /** LOT-ovi sa stanja za stavku: usaglašeni, SS samo za kupca u Srbiji, sa onim što ovaj nalog sme da uzme. */
  lotOptions: (orderId: string, lineId: string) =>
    apiFetch<LotOptions>(`/dispatch-orders/quality/${orderId}/lines/${lineId}/lot-options`),

  /** Nalozi koji danas čekaju odluku; preneta istorija iz ERP-a nije u spisku. */
  awaiting: () => apiFetch<DispatchOrder[]>("/dispatch-orders/quality/awaiting"),

  get: (id: string) => apiFetch<DispatchOrder>(`/dispatch-orders/quality/${id}`),

  assignLot: (orderId: string, lineId: string, body: { goodsLotId: string | null; pallets?: number | null }) =>
    apiFetch<DispatchOrder>(`/dispatch-orders/quality/${orderId}/lines/${lineId}/lot`, {
      method: "PUT",
      body,
    }),

  release: (orderId: string, lineId: string) =>
    apiFetch<DispatchOrder>(`/dispatch-orders/quality/${orderId}/lines/${lineId}/release`, {
      method: "POST",
    }),

  hold: (orderId: string, lineId: string, reason: string) =>
    apiFetch<DispatchOrder>(`/dispatch-orders/quality/${orderId}/lines/${lineId}/hold`, {
      method: "POST",
      body: { reason },
    }),
};

/* ------------------------------------------------------------------ strana prodaje */

/**
 * Prodaja otvara nalog, popisuje šta ide na njega i šalje ga kvalitetu.
 *
 * Ovde NEMA polja za LOT, i to je namerno: koji LOT ide odlučuje kvalitet, a polje ovde bi dalo prodaji
 * da odgovori na pitanje zbog kojeg cela kapija postoji.
 */
export type CreateDispatchOrderBody = {
  companyId: string;
  sourceOrderId?: string | null;
  sourceOfferId?: string | null;
  issueDate?: string | null;
  dispositionDate?: string | null;
  vehicle?: string | null;
  driver?: string | null;
  loadingPlace?: string | null;
  deliveryPlace?: string | null;
  unloadingPlace?: string | null;
  contactPerson?: string | null;
  country?: string | null;
  warehouse?: string | null;
  note?: string | null;
};

export type DispatchLineBody = {
  erpProductId?: string | null;
  productName: string;
  quantity: number;
  unit?: string | null;
};

/** A line of a dispatch note the ERP issued: what left, and from which lot. */
export type DispatchNoteLine = {
  erpProductId: string | null;
  productName: string | null;
  quantity: number | null;
  unit: string | null;
  lotId: string | null;
  lot: string | null;
  /** Status kvaliteta LOT-a, samo za Bankomovo osoblje; kupac dobija null. */
  lotQualityStatus?: string | null;
};

/** An invoice of a dispatch note. Amounts and state are null for whoever may not read the customer's finance. */
export type DispatchNoteInvoice = {
  id: string;
  number: string;
  issueDate: string | null;
  currency: string | null;
  grossAmount: number | null;
  openAmount: number | null;
  status: string | null;
};

/** A dispatch note the ERP issued on one of an order's dispatch orders, with the invoices that bill it. */
export type DispatchNote = {
  id: string;
  number: string;
  issueDate: string | null;
  dispatchOrderId: string;
  lines: DispatchNoteLine[];
  invoices: DispatchNoteInvoice[];
};

/** The buyer's side: what left for one of his own orders and what was billed for it. */
export const myShipmentApi = {
  forOrder: (orderId: string) => apiFetch<DispatchNote[]>(`/me/orders/${orderId}/shipments`),
};

export const dispatchOrderApi = {
  /** Nalozi koji žive u portalu i nisu završeni; 108.751 prenet iz ERP-a nije u spisku. */
  list: () => apiFetch<DispatchOrder[]>("/dispatch-orders"),

  get: (id: string) => apiFetch<DispatchOrder>(`/dispatch-orders/${id}`),

  /** The dispatch orders made from a portal order. */
  forOrder: (orderId: string) => apiFetch<DispatchOrder[]>(`/dispatch-orders/for-order/${orderId}`),

  /** The dispatch notes and invoices the ERP issued on an order's dispatch orders. */
  notesForOrder: (orderId: string) => apiFetch<DispatchNote[]>(`/dispatch-orders/for-order/${orderId}/notes`),

  /** Opens the dispatch order of an approved order, with its lines and delivery. */
  createFromOrder: (orderId: string) =>
    apiFetch<DispatchOrder>(`/dispatch-orders/from-order/${orderId}`, { method: "POST" }),

  create: (body: CreateDispatchOrderBody) =>
    apiFetch<DispatchOrder>("/dispatch-orders", { method: "POST", body }),

  update: (id: string, body: Omit<CreateDispatchOrderBody, "companyId" | "sourceOrderId" | "sourceOfferId">) =>
    apiFetch<DispatchOrder>(`/dispatch-orders/${id}`, { method: "PUT", body }),

  addLine: (id: string, body: DispatchLineBody) =>
    apiFetch<DispatchOrder>(`/dispatch-orders/${id}/lines`, { method: "POST", body }),

  removeLine: (id: string, lineId: string) =>
    apiFetch<DispatchOrder>(`/dispatch-orders/${id}/lines/${lineId}`, { method: "DELETE" }),

  sendToQuality: (id: string) =>
    apiFetch<DispatchOrder>(`/dispatch-orders/${id}/send-to-quality`, { method: "POST" }),

  /** Vraća nalog prodaji i briše svaku odluku kvaliteta na njemu — to brisanje je poenta. */
  returnToDraft: (id: string) =>
    apiFetch<DispatchOrder>(`/dispatch-orders/${id}/return-to-draft`, { method: "POST" }),

  cancel: (id: string) => apiFetch<DispatchOrder>(`/dispatch-orders/${id}/cancel`, { method: "POST" }),
};
